 
/**
 * S26 · Permissions — first-run gate. Walks Sarah through camera, mic,
 * and location requests so she arrives on the Session Hub with
 * everything pre-authorised. Writes the `kapture.hasOnboarded` flag to
 * localStorage so subsequent visits skip the gate.
 *
 * This is an initial route the user can also access later via a header
 * link — we don't redirect away from it automatically.
 */
import { useEffect, useState } from 'react';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import {
  Btn,
  Chip,
  GestureBar,
  I,
  Ico,
  ScreenFrame,
  StatusBar,
  T,
} from '@/components/primitives';
import {
  markOnboarded,
  type PermState,
  probeCamera,
  probeGeo,
  probeMic,
  probeStorage,
  requestCamera,
  requestGeo,
  requestMic,
  requestPersistentStorage,
} from '@/lib/device/permissions';

export const Route = createFileRoute('/onboarding/permissions')({
  component: PermissionsPage,
  staticData: { mobile: true },
});

interface PermRow {
  id: 'camera' | 'mic' | 'geo' | 'storage';
  label: string;
  detail: string;
  icon: string | React.ReactNode;
  probe(): Promise<PermState>;
  request(): Promise<PermState>;
}

const ROWS: PermRow[] = [
  {
    id: 'camera',
    label: 'Camera',
    detail: 'Photos, video clips, barcode scans, and signature receipts.',
    icon: I.camera,
    probe: probeCamera,
    request: requestCamera,
  },
  {
    id: 'mic',
    label: 'Microphone',
    // Video narration belongs here, not only in the camera row. Refusing the
    // microphone does not block a recording — the recorder falls back to a
    // silent clip and says so — so an inspector who declines here needs to
    // know beforehand that the pan they are about to narrate will have no
    // narration on it.
    detail: 'Voice notes and video narration. Audio stays on-device until you sync.',
    icon: I.mic,
    probe: probeMic,
    request: requestMic,
  },
  {
    id: 'geo',
    label: 'Location',
    detail: 'Auto-tags captures with GPS fix and accuracy.',
    icon: I.pin,
    probe: probeGeo,
    request: requestGeo,
  },
  {
    // Last in the list and the least visible, but the one whose refusal costs
    // the most: everything the other three capture is written to this device
    // and stays there until sync. Without persistence the browser is entitled
    // to reclaim that storage when it is short of space.
    //
    // Usually already 'granted' by the time this screen renders — `main.tsx`
    // asks at boot and Chrome answers silently. The row exists for the engines
    // that prompt, and so that a denial is visible now rather than a surprise
    // on the day it matters.
    id: 'storage',
    label: 'Keep data on device',
    detail: 'Without this the browser may discard unsynced captures if it runs short of space. Sync before you leave site.',
    icon: I.folder,
    probe: probeStorage,
    request: requestPersistentStorage,
  },
];

function PermissionsPage() {
  const navigate = useNavigate();
  const [states, setStates] = useState<Record<string, PermState>>({
    camera: 'unknown',
    mic: 'unknown',
    geo: 'unknown',
    storage: 'unknown',
  });
  const [requesting, setRequesting] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    void Promise.all(
      ROWS.map(async (row) => ({ id: row.id, state: await row.probe() })),
    ).then((results) => {
      if (cancelled) return;
      setStates(
        Object.fromEntries(results.map((r) => [r.id, r.state])) as Record<string, PermState>,
      );
    });
    return () => {
      cancelled = true;
    };
  }, []);

  async function requestOne(row: PermRow) {
    setRequesting(row.id);
    const state = await row.request();
    setStates((prev) => ({ ...prev, [row.id]: state }));
    setRequesting(null);
  }

  async function requestAll() {
    for (const row of ROWS) {
      setRequesting(row.id);
      const state = await row.request();
      setStates((prev) => ({ ...prev, [row.id]: state }));
    }
    setRequesting(null);
  }

  function finish() {
    markOnboarded();
    navigate({ to: '/' });
  }

  const allResolved = ROWS.every((r) => states[r.id] && states[r.id] !== 'unknown');

  return (
    <ScreenFrame tone="light">
      <StatusBar />
      <div
        style={{
          padding: '10px 16px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          borderBottom: `1px solid ${T.stroke}`,
        }}
      >
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 15, fontWeight: 700, fontFamily: T.font }}>Allow access</div>
          <div style={{ fontSize: 11, color: T.textMute, fontFamily: T.font }}>
            One-time setup · you can change these later
          </div>
        </div>
        <Chip tone="brandSoft" size="sm">
          Step 1 of 1
        </Chip>
      </div>

      <div style={{ flex: 1, overflow: 'auto', padding: 16 }}>
        <div
          style={{
            padding: 14,
            borderRadius: 14,
            background: T.ink,
            color: '#fff',
            marginBottom: 16,
          }}
        >
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 10 }}>
            {ROWS.map((row) => {
              const stateChip =
                states[row.id] === 'granted'
                  ? 'good'
                  : states[row.id] === 'denied'
                    ? 'danger'
                    : states[row.id] === 'unavailable'
                      ? 'warn'
                      : 'neutral';
              return (
                <Chip key={row.id} tone={stateChip} size="sm">
                  {row.label} · {states[row.id] ?? 'unknown'}
                </Chip>
              );
            })}
          </div>
          <div style={{ fontSize: 13, lineHeight: 1.55 }}>
            Kapture captures photos, voice, and GPS straight to this device's local
            storage. Nothing leaves until you hit sync — then only the signed
            report is uploaded.
          </div>
        </div>

        {ROWS.map((row) => (
          <PermissionRow
            key={row.id}
            row={row}
            state={states[row.id] ?? 'unknown'}
            busy={requesting === row.id}
            onRequest={() => void requestOne(row)}
          />
        ))}
      </div>

      <div
        style={{
          padding: '10px 16px 12px',
          borderTop: `1px solid ${T.stroke}`,
          display: 'flex',
          gap: 8,
        }}
      >
        <Btn
          tone="light"
          size="md"
          disabled={requesting != null}
          onClick={() => void requestAll()}
          style={{ opacity: requesting ? 0.5 : 1 }}
        >
          Allow all
        </Btn>
        <Btn
          tone="primary"
          size="md"
          full
          iconRight={<Ico d={I.arrow} size={16} />}
          onClick={finish}
          disabled={!allResolved || requesting != null}
          style={{ opacity: allResolved && !requesting ? 1 : 0.5 }}
        >
          Continue to sessions
        </Btn>
      </div>
      <GestureBar />
    </ScreenFrame>
  );
}

function PermissionRow({
  row,
  state,
  busy,
  onRequest,
}: {
  row: PermRow;
  state: PermState;
  busy: boolean;
  onRequest(): void;
}) {
  const tone: 'good' | 'danger' | 'warn' | 'neutral' =
    state === 'granted' ? 'good' : state === 'denied' ? 'danger' : state === 'unavailable' ? 'warn' : 'neutral';
  return (
    <div
      style={{
        padding: 14,
        borderRadius: 12,
        border: `1px solid ${T.stroke}`,
        background: T.surface,
        marginBottom: 10,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        boxShadow: T.sh,
      }}
    >
      <div
        style={{
          width: 44,
          height: 44,
          borderRadius: 10,
          background: state === 'granted' ? T.goodSoft : T.brandSoft,
          color: state === 'granted' ? T.good : T.brand,
          display: 'grid',
          placeItems: 'center',
          flexShrink: 0,
        }}
      >
        <Ico d={row.icon} size={20} />
      </div>
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
          <div style={{ fontSize: 14, fontWeight: 700, fontFamily: T.font, letterSpacing: '-0.01em' }}>
            {row.label}
          </div>
          <Chip tone={tone} size="sm">
            {state}
          </Chip>
        </div>
        <div style={{ fontSize: 12, color: T.textDim, lineHeight: 1.5 }}>{row.detail}</div>
        <div style={{ marginTop: 10, display: 'flex', gap: 8 }}>
          <Btn
            tone={state === 'granted' ? 'good' : 'primary'}
            size="sm"
            onClick={onRequest}
            disabled={busy || state === 'unavailable' || state === 'granted'}
            style={{ opacity: busy || state === 'unavailable' || state === 'granted' ? 0.6 : 1 }}
          >
            {state === 'granted' ? 'Allowed' : busy ? 'Requesting…' : state === 'denied' ? 'Retry' : 'Allow'}
          </Btn>
        </div>
      </div>
    </div>
  );
}
